import { gql } from "@apollo/client";
import {
  INTERRUPT_APP_SESSION,
  RESTART_AND_RUN_APP_SESSION,
  RESTART_APP_SESSION,
  RUN_APP_SESSION,
  STOP_APP_SESSION,
  stableStringify,
} from "@hex/common";
import React, { useCallback } from "react";

import { useToaster } from "../../components/common/Toasts";
import { LinkIcon } from "../../components/icons/CustomIcons";
import { getViewSelectors } from "../../components/saved-views/useGetViewSelectors";
import { Flex, Txt } from "../../hex-components";
import { datadogSite } from "../../util/data";
import { useDispatch, useStore } from "../../redux/hooks";
import { appSessionMPSelectors } from "../../redux/slices/appSessionMPSlice";
import { appViewActions } from "../../redux/slices/appViewSlice";
import { hexMPSelectors } from "../../redux/slices/hexMPSlice";
import { savedViewDefaultAppSessionSelectors } from "../../redux/slices/savedViewDefaultAppSessionSlice";
import { useAppSessionAOContext } from "../../util/appSessionAOContext";
import { useProjectContext } from "../../util/projectContext";
import { useSessionContext } from "../../util/sessionContext";

import {
  useRunEventBasedAppSessionMutation,
  useUpdateDefaultAppSessionMutation,
} from "./useAppControls.generated";

gql`
  mutation RunEventBasedAppSession(
    $appSessionId: AppSessionId!
    $executeTrace: Boolean
    $forceOverwriteCache: Boolean
  ) {
    runEventBasedAppSession(
      appSessionId: $appSessionId
      executeTrace: $executeTrace
      forceOverwriteCache: $forceOverwriteCache
    )
  }

  mutation UpdateDefaultAppSession(
    $hexId: HexId!
    $savedViewId: SavedViewId
    $overwriteCache: Boolean
  ) {
    runLastPublishedVersionForHex(
      hexId: $hexId
      savedViewId: $savedViewId
      overwriteCache: $overwriteCache
    ) {
      id
    }
  }
`;

interface UseAppControlsResult {
  run: () => Promise<void>;
  runAndOverwriteCache: () => Promise<void>;
  runWithTrace: () => Promise<void>;
  runWithTraceAndOverwriteCache: () => Promise<void>;
  runAndRefreshApp: () => Promise<void>;
  kill: () => Promise<void>;
  interrupt: () => Promise<void>;
  restart: () => Promise<void>;
  restartAndRun: () => Promise<void>;
}

export function useAppControls(): UseAppControlsResult {
  const store = useStore();
  const dispatch = useDispatch();
  const { hexId, hexVersionId } = useProjectContext();
  const { appSessionId } = useSessionContext();
  const { dispatchAO } = useAppSessionAOContext();
  const toaster = useToaster();

  const [runEventBasedAppSession] = useRunEventBasedAppSessionMutation();

  const runInternal = useCallback(
    async ({
      executeTrace = false,
      forceOverwriteCache = false,
    }: {
      forceOverwriteCache?: boolean;
      executeTrace?: boolean;
    }) => {
      const state = store.getState();

      const appSession = appSessionMPSelectors
        .getAppSessionSelectors(appSessionId)
        .select(state);

      dispatch(
        appViewActions.setLastRunInputValues(
          stableStringify(
            getViewSelectors(
              hexVersionId,
              appSessionId,
            ).selectAllHumanReadableDiffValues(state),
          ),
        ),
      );

      if (appSession.useEvents) {
        await runEventBasedAppSession({
          variables: {
            appSessionId,
            executeTrace: executeTrace,
            forceOverwriteCache,
          },
          context: {
            enableTracing: executeTrace,
          },
        });
      } else {
        await dispatchAO(
          RUN_APP_SESSION.create({ forceOverwriteCache, executeTrace }),
          {
            enableTracing: executeTrace,
          },
        ).promise;
      }
    },
    [
      appSessionId,
      dispatch,
      dispatchAO,
      hexVersionId,
      runEventBasedAppSession,
      store,
    ],
  );

  const run = useCallback(async () => {
    await runInternal({});
  }, [runInternal]);

  const runAndOverwriteCache = useCallback(async () => {
    await runInternal({ forceOverwriteCache: true });
  }, [runInternal]);

  const showTraceToast = useCallback(() => {
    toaster.show({
      message: (
        <div css="width: 350px">
          <p>
            <Txt fontWeight="semi_bold">Go to Datadog to see the trace.</Txt>
          </p>
          <p>
            It may take a few minutes after cells have finished running for the
            trace to show up in Datadog.
          </p>
        </div>
      ),
      intent: "primary",
      action: {
        text: (
          <Flex align="center" gap={4}>
            <LinkIcon />
            <span>Open Datadog</span>
          </Flex>
        ),
        onClick: () => {
          const ddSite = datadogSite ?? "datadoghq.com";
          window.open(
            `https://app.${ddSite}/apm/traces?query=service%3Ahex.sidecar%20operation_name%3AenqueuedBatch%20%40hexId%3A${hexId}&agg_m=count&agg_m_source=base&agg_t=count&cols=service%2Cresource_name%2C%40duration%2C%40_span.count%2C%40_duration.by_service%2C%40orgId%2C%40hexId%2C%40&saved-view-id=3299621&sort=desc&spanType=all`,
            "_blank",
          );
        },
      },
      timeout: 30_000,
    });
  }, [hexId, toaster]);

  const runWithTrace = useCallback(async () => {
    await runInternal({ executeTrace: true });
    showTraceToast();
  }, [runInternal, showTraceToast]);

  const runWithTraceAndOverwriteCache = useCallback(async () => {
    await runInternal({ executeTrace: true, forceOverwriteCache: true });
    showTraceToast();
  }, [runInternal, showTraceToast]);

  const [updateDefaultAppSession] = useUpdateDefaultAppSessionMutation();
  const runAndRefreshApp = useCallback(async () => {
    const state = store.getState();
    const hex = hexMPSelectors.getHexSelectors(hexId).select(state);
    const savedViewId =
      savedViewDefaultAppSessionSelectors.selectCurrentSavedViewId(state) ??
      null;

    if (hex.cacheDefaultAppState) {
      await Promise.all([
        runInternal({ forceOverwriteCache: true }),
        updateDefaultAppSession({
          variables: {
            hexId,
            savedViewId,
            // maclockard: I find this a little bit confusing personally
            // but this was the prior behavior to my refactor so I'm going to leave it
            overwriteCache: false,
          },
        }),
      ]);
    }
  }, [hexId, runInternal, store, updateDefaultAppSession]);

  const interrupt = useCallback(async () => {
    await dispatchAO(INTERRUPT_APP_SESSION.create()).promise;
  }, [dispatchAO]);

  const kill = useCallback(async () => {
    await dispatchAO(
      STOP_APP_SESSION.create({
        errored: false,
        kernelStatus: undefined,
        killed: true,
      }),
    ).promise;
  }, [dispatchAO]);

  const restart = useCallback(async () => {
    await dispatchAO(RESTART_APP_SESSION.create()).promise;
  }, [dispatchAO]);

  const restartAndRun = useCallback(async () => {
    await dispatchAO(RESTART_AND_RUN_APP_SESSION.create()).promise;
  }, [dispatchAO]);

  return {
    run,
    runAndOverwriteCache,
    kill,
    interrupt,
    runWithTrace,
    runWithTraceAndOverwriteCache,
    runAndRefreshApp,
    restart,
    restartAndRun,
  };
}
